/**
 * Centralized logging utility
 *
 * Provides type-safe logging with 5 levels: DEBUG, INFO, WARN, ERROR, TEMP
 * Includes environment-based configuration and color-coded console output
 */

export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    TEMP = 4, // Special temporary debugging level
}

interface LoggerConfig {
    level: LogLevel;
    tempEnabled: boolean;
}

interface Logger {
    debug(message: string, data?: unknown): void;
    info(message: string, data?: unknown): void;
    warn(message: string, data?: unknown): void;
    error(message: string, data?: unknown): void;
    temp(message: string, data?: unknown): void;
}

// Color and style configurations for each log level
const LOG_STYLES = {
    [LogLevel.DEBUG]: {
        icon: '🔍',
        color: '',
        background: '',
        label: 'DEBUG',
    },
    [LogLevel.INFO]: {
        icon: 'ℹ️',
        color: '',
        background: '',
        label: 'INFO',
    },
    [LogLevel.WARN]: {
        icon: '⚠️',
        color: '',
        background: '',
        label: 'WARN',
    },
    [LogLevel.ERROR]: {
        icon: '❌',
        color: '',
        background: '',
        label: 'ERROR',
    },
    [LogLevel.TEMP]: {
        icon: '⚡',
        color: '',
        background: '',
        label: 'TEMP',
    },
};

/**
 * Parse log level from string
 */
function parseLogLevel(level: string): LogLevel {
    const normalized = level.toUpperCase();
    switch (normalized) {
        case 'DEBUG':
            return LogLevel.DEBUG;
        case 'INFO':
            return LogLevel.INFO;
        case 'WARN':
            return LogLevel.WARN;
        case 'ERROR':
            return LogLevel.ERROR;
        default:
            return LogLevel.INFO; // Safe default
    }
}

/**
 * Load logger configuration from environment variables
 */
function loadConfig(): LoggerConfig {
    const isDevelopment = import.meta.env.DEV;

    // Default log level: DEBUG in development, INFO in production
    const defaultLevel = isDevelopment ? 'DEBUG' : 'INFO';
    const levelStr = import.meta.env.VITE_LOG_LEVEL || defaultLevel;

    // TEMP logs: enabled by default in development, disabled in production
    // Can be explicitly disabled by setting VITE_LOG_TEMP_ENABLED=false
    const tempEnabled = isDevelopment &&
                       import.meta.env.VITE_LOG_TEMP_ENABLED !== 'false';

    return {
        level: parseLogLevel(levelStr),
        tempEnabled,
    };
}

// Global configuration loaded once
const config: LoggerConfig = loadConfig();

/**
 * Check if a log level should be output
 */
function shouldLog(level: LogLevel): boolean {
    return level >= config.level;
}

/**
 * Format timestamp as HH:MM:SS.mmm
 */
function formatTimestamp(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * Get CSS style string for a log level
 */
function getStyle(level: LogLevel): string {
    const style = LOG_STYLES[level];
    const styles: string[] = [];

    if (style.color) {
        styles.push(`color: ${style.color}`);
    }
    if (style.background) {
        styles.push(`background: ${style.background}`);
    }
    styles.push('font-weight: bold');
    styles.push('padding: 2px 4px');
    styles.push('border-radius: 2px');

    return styles.join('; ');
}

/**
 * Format log message with icon, level, module, and timestamp
 */
function formatMessage(level: LogLevel, module: string, message: string): string {
    const style = LOG_STYLES[level];
    const timestamp = formatTimestamp();
    return `${style.icon} [${style.label}] [${module}] ${timestamp} | ${message}`;
}

/**
 * Core logging function
 */
function log(level: LogLevel, module: string, message: string, data?: unknown): void {
    // Early exit if this log level is disabled
    if (!shouldLog(level)) {
        return;
    }

    const formattedMessage = formatMessage(level, module, message);
    const style = getStyle(level);

    // Use appropriate console method based on log level
    switch (level) {
        case LogLevel.ERROR:
            if (data !== undefined) {
                console.error(`%c${formattedMessage}`, style, data);
            } else {
                console.error(`%c${formattedMessage}`, style);
            }
            break;
        case LogLevel.WARN:
            if (data !== undefined) {
                console.warn(`%c${formattedMessage}`, style, data);
            } else {
                console.warn(`%c${formattedMessage}`, style);
            }
            break;
        default:
            if (data !== undefined) {
                console.log(`%c${formattedMessage}`, style, data);
            } else {
                console.log(`%c${formattedMessage}`, style);
            }
            break;
    }
}

/**
 * Handle TEMP level logging with special toggle logic
 */
function logTemp(module: string, message: string, data?: unknown): void {
    // Check if TEMP logs are enabled
    if (!config.tempEnabled) {
        return;
    }

    const formattedMessage = formatMessage(LogLevel.TEMP, module, message);
    const style = getStyle(LogLevel.TEMP);

    if (data !== undefined) {
        console.log(`%c${formattedMessage}`, style, data);
    } else {
        console.log(`%c${formattedMessage}`, style);
    }
}

/**
 * Create a scoped logger for a specific module
 *
 * @param moduleName - Name of the module (e.g., 'useAuth', 'geometry', 'PdfCanvas')
 * @returns Logger instance with scoped methods
 *
 * @example
 * ```typescript
 * import { createLogger } from '@/utils/logger';
 *
 * const log = createLogger('useAuth');
 *
 * log.debug('Loading user profile', { userId });
 * log.info('User authenticated', { email: user.email });
 * log.warn('Token expiring soon', { expiresIn: 60 });
 * log.error('Authentication failed', error);
 * log.temp('Debugging auth flow', { state, data });
 * ```
 */
export function createLogger(moduleName: string): Logger {
    return {
        debug: (message: string, data?: unknown) => log(LogLevel.DEBUG, moduleName, message, data),
        info: (message: string, data?: unknown) => log(LogLevel.INFO, moduleName, message, data),
        warn: (message: string, data?: unknown) => log(LogLevel.WARN, moduleName, message, data),
        error: (message: string, data?: unknown) => log(LogLevel.ERROR, moduleName, message, data),
        temp: (message: string, data?: unknown) => logTemp(moduleName, message, data),
    };
}
